import { supabase } from '@/lib/supabase/client';

/**
 * Debug why Georgia clusters aren't being generated
 * Step through the exact cluster generation logic
 */

const GA_BOUNDS = {
  north: 35.0,
  south: 30.3,
  east: -80.8,
  west: -85.6
};

export async function debugGeorgiaClusterGeneration() {
  console.log('🔍 Debugging Georgia cluster generation...');

  try {
    // 1. Get raw GA data from materialized view
    console.log('📊 Step 1: Getting raw GA data from materialized view...');

    const { data: rawData, error } = await supabase
      .from('cars_with_safety_recalls_mv')
      .select(
        `
        service_provider_name,
        seller_latitude,
        seller_longitude,
        seller_street,
        seller_city,
        seller_region,
        seller_postal_code,
        vin,
        safety_recalls
      `
      )
      .eq('seller_region', 'GA')
      .limit(100);

    if (error) {
      console.error('❌ Error getting raw GA data:', error);
      return;
    }

    console.log(`✅ Found ${rawData?.length || 0} raw GA records`);

    if (!rawData || rawData.length === 0) {
      console.log(
        '❌ No GA data found in materialized view. Problem confirmed: data is missing from the view.'
      );
      return;
    }

    console.log('📍 Sample GA records:');
    rawData.slice(0, 3).forEach((record, i) => {
      console.log(`  ${i + 1}. ${record.service_provider_name} - ${record.seller_city}`);
      console.log(`     Coords: ${record.seller_latitude}, ${record.seller_longitude}`);
      console.log(`     Safety Recalls Type: ${typeof record.safety_recalls}`);
      if (record.safety_recalls) {
        console.log(
          `     Safety Recalls Length: ${Array.isArray(record.safety_recalls) ? record.safety_recalls.length : 'Not array'}`
        );
      }
    });

    // 2. Test coordinate filtering
    console.log('\n📍 Step 2: Testing coordinate filtering...');

    const coordFiltered = rawData.filter(row => {
      return (
        row.seller_latitude >= GA_BOUNDS.south &&
        row.seller_latitude <= GA_BOUNDS.north &&
        row.seller_longitude >= GA_BOUNDS.west &&
        row.seller_longitude <= GA_BOUNDS.east &&
        row.seller_latitude !== null &&
        row.seller_longitude !== null
      );
    });

    console.log(
      `📍 ${coordFiltered.length}/${rawData.length} GA records pass coordinate filtering`
    );

    if (coordFiltered.length < rawData.length) {
      console.log('🚨 Some GA records filtered out by coordinates:');
      const outOfBounds = rawData.filter(
        row =>
          !(
            row.seller_latitude >= GA_BOUNDS.south &&
            row.seller_latitude <= GA_BOUNDS.north &&
            row.seller_longitude >= GA_BOUNDS.west &&
            row.seller_longitude <= GA_BOUNDS.east
          )
      );

      outOfBounds.slice(0, 3).forEach((record, i) => {
        console.log(`  ${i + 1}. ${record.service_provider_name} - ${record.seller_city}`);
        console.log(
          `     Coords: ${record.seller_latitude}, ${record.seller_longitude} (out of bounds)`
        );
      });
    }

    // 3. Test JSON parsing and M/N filtering
    console.log('\n🔍 Step 3: Testing safety_recalls processing...');

    let jsonParseErrors = 0;
    let mnFilteredOut = 0;
    let validRecalls = 0;
    const validRows: any[] = [];

    coordFiltered.forEach((row, index) => {
      try {
        let safetyRecalls = row.safety_recalls;

        // JSON parsing test
        if (typeof safetyRecalls === 'string') {
          try {
            safetyRecalls = JSON.parse(safetyRecalls);
          } catch (parseError) {
            jsonParseErrors++;
            console.log(`❌ Row ${index + 1}: JSON parse error for VIN ${row.vin}`);
            return;
          }
        }

        // M/N filtering test
        if (safetyRecalls && Array.isArray(safetyRecalls)) {
          const validRecallsForRow = safetyRecalls.filter((recall: any) => {
            const campaignNumber = recall.campaign_number || '';
            return !campaignNumber.includes('M') && !campaignNumber.includes('N');
          });

          if (validRecallsForRow.length === 0) {
            mnFilteredOut++;
            console.log(
              `🚫 Row ${index + 1}: All recalls filtered out by M/N filter for VIN ${row.vin}`
            );
            console.log(
              `   Campaign numbers: ${safetyRecalls.map((r: any) => r.campaign_number).join(', ')}`
            );
            return;
          }

          validRecalls++;
          validRows.push({
            ...row,
            validRecallCount: validRecallsForRow.length,
            totalRecallCount: safetyRecalls.length
          });
        } else {
          console.log(
            `❌ Row ${index + 1}: safety_recalls is not an array for VIN ${row.vin}`,
            typeof safetyRecalls
          );
        }
      } catch (error) {
        console.error(`❌ Row ${index + 1}: Error processing VIN ${row.vin}:`, error);
      }
    });

    console.log('📊 Processing results:');
    console.log(`  ✅ Valid rows after all filtering: ${validRecalls}`);
    console.log(`  ❌ JSON parse errors: ${jsonParseErrors}`);
    console.log(`  🚫 M/N filtered out: ${mnFilteredOut}`);

    if (validRows.length > 0) {
      console.log('\n✅ Sample valid rows that should create clusters:');
      validRows.slice(0, 3).forEach((row, i) => {
        console.log(`  ${i + 1}. ${row.service_provider_name} - ${row.seller_city}`);
        console.log(`     Valid recalls: ${row.validRecallCount}/${row.totalRecallCount}`);
      });
    }

    // 4. Test dealer grouping
    console.log('\n🏪 Step 4: Testing dealer grouping...');

    const dealerMap = new Map();

    validRows.forEach(row => {
      const dealerKey = `${row.service_provider_name}_${row.seller_latitude}_${row.seller_longitude}`;

      if (!dealerMap.has(dealerKey)) {
        dealerMap.set(dealerKey, {
          name: row.service_provider_name || 'Unknown',
          lat: row.seller_latitude,
          lon: row.seller_longitude,
          vins: new Set(),
          recallCount: 0
        });
      }

      const dealer = dealerMap.get(dealerKey);
      dealer.vins.add(row.vin);
      dealer.recallCount += row.validRecallCount;
    });

    console.log(`🏪 ${dealerMap.size} unique dealers would be created`);

    if (dealerMap.size > 0) {
      console.log('🏪 Dealers that would create clusters:');
      Array.from(dealerMap.entries())
        .slice(0, 3)
        .forEach(([key, dealer], i) => {
          console.log(`  ${i + 1}. ${dealer.name}`);
          console.log(`     VINs: ${dealer.vins.size}, Recalls: ${dealer.recallCount}`);
        });
    }

    return {
      totalGA: rawData.length,
      coordFiltered: coordFiltered.length,
      jsonErrors: jsonParseErrors,
      mnFiltered: mnFilteredOut,
      validRows: validRows.length,
      uniqueDealers: dealerMap.size
    };
  } catch (error) {
    console.error('❌ Debug failed:', error);
    return null;
  }
}

// Export to window for console access
if (typeof window !== 'undefined') {
  (window as any).debugGeorgiaClusterGeneration = debugGeorgiaClusterGeneration;
}

export default debugGeorgiaClusterGeneration;
